/**
 * API utility functions for SurviveIt
 * Handles HTTP requests to Cloudflare Workers API
 * Updated for v1 API endpoints - 2024-11-18
 */

import logger from './logger'

// Always use relative URLs for Cloudflare Pages Functions
const API_BASE_URL = '/api'
const API_VERSION = '/v1'

class APIError extends Error {
  constructor(message, status, data) {
    super(message)
    this.name = 'APIError'
    this.status = status
    this.data = data
  }
}

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken')
}

// Set auth token in localStorage
const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token)
  } else {
    localStorage.removeItem('authToken')
  }
}

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`
  const token = getAuthToken()

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  }

  // Add authorization header if token exists
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  try {
    const startTime = performance.now()
    const response = await fetch(url, config)
    const endTime = performance.now()

    // Handle non-JSON responses (for CSV/PDF exports)
    const contentType = response.headers.get('content-type')
    let data

    if (contentType && contentType.includes('application/json')) {
      data = await response.json()
    } else if (contentType && (contentType.includes('text/csv') || contentType.includes('text/html'))) {
      data = await response.text()
    } else {
      data = await response.blob()
    }

    // Log API performance
    logger.performance('API Request', endTime - startTime, {
      endpoint,
      method: config.method || 'GET',
      status: response.status,
      duration: endTime - startTime
    })

    if (!response.ok) {
      const errorMessage = typeof data === 'object' ? data.error : 'Request failed'

      logger.apiError(endpoint, new Error(errorMessage), {
        method: config.method || 'GET',
        status: response.status,
        body: config.body
      })

      throw new APIError(
        errorMessage,
        response.status,
        data
      )
    }

    // Log successful API request in debug mode
    logger.debug('API Request Successful', {
      endpoint,
      method: config.method || 'GET',
      status: response.status,
      duration: endTime - startTime
    })

    return data
  } catch (error) {
    if (error instanceof APIError) {
      throw error
    }

    // Network or other errors
    logger.apiError(endpoint, error, {
      method: config.method || 'GET',
      type: 'network_error',
      body: config.body
    })

    throw new APIError(
      error.message || 'Network error',
      0,
      null
    )
  }
}

// Authentication API
export const authAPI = {
  register: async (userData) => {
    const response = await apiRequest(`${API_VERSION}/auth/register`, {
      method: 'POST',
      body: JSON.stringify(userData),
    })

    if (response.token) {
      setAuthToken(response.token)
    }

    return response
  },

  login: async (credentials) => {
    const response = await apiRequest(`${API_VERSION}/auth/login`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    })

    if (response.token) {
      setAuthToken(response.token)
    }

    return response
  },

  logout: () => {
    setAuthToken(null)
  },

  verifyToken: async () => {
    return await apiRequest(`${API_VERSION}/auth/verify`)
  },

  forgotPassword: async (email) => {
    return await apiRequest(`${API_VERSION}/auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  resetPassword: async (token, newPassword) => {
    return await apiRequest(`${API_VERSION}/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    })
  },
}

// User API
export const userAPI = {
  getProfile: async () => {
    return await apiRequest(`${API_VERSION}/users/profile`)
  },

  updateProfile: async (userData) => {
    return await apiRequest(`${API_VERSION}/users/profile`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    })
  },

  getProgress: async () => {
    return await apiRequest(`${API_VERSION}/users/progress`)
  },

  getAnalytics: async () => {
    return await apiRequest(`${API_VERSION}/users/analytics`)
  },

  // Family features
  generateFamilyCode: async () => {
    return await apiRequest(`${API_VERSION}/users/family/generate`, {
      method: 'POST',
    })
  },

  joinFamily: async (familyCode) => {
    return await apiRequest(`${API_VERSION}/users/family/join`, {
      method: 'POST',
      body: JSON.stringify({ familyCode }),
    })
  },

  getFamily: async () => {
    return await apiRequest(`${API_VERSION}/users/family`)
  },

  leaveFamily: async () => {
    return await apiRequest(`${API_VERSION}/users/family/leave`, {
      method: 'POST',
    })
  },

  getLeaderboard: async () => {
    return await apiRequest(`${API_VERSION}/users/leaderboard`)
  },
}

// Modules & Lessons API
export const moduleAPI = {
  getAll: async () => {
    return await apiRequest(`${API_VERSION}/modules`)
  },

  getById: async (moduleId) => {
    return await apiRequest(`${API_VERSION}/modules/${moduleId}`)
  },

  getProgress: async (moduleId) => {
    return await apiRequest(`${API_VERSION}/modules/${moduleId}/progress`)
  },
}

// Lessons API
export const lessonAPI = {
  completeLesson: async (lessonId, data) => {
    return await apiRequest(`${API_VERSION}/modules/lessons/${lessonId}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  submitQuiz: async (lessonId, answers) => {
    return await apiRequest(`${API_VERSION}/modules/lessons/${lessonId}/quiz`, {
      method: 'POST',
      body: JSON.stringify(answers),
    })
  },
}

// Daily Tasks API
export const tasksAPI = {
  getDailyTasks: async () => {
    return await apiRequest(`${API_VERSION}/users/tasks/daily`)
  },

  completeTask: async (taskId) => {
    return await apiRequest(`${API_VERSION}/users/tasks/${taskId}/complete`, {
      method: 'POST',
    })
  },

  getTaskHistory: async () => {
    return await apiRequest(`${API_VERSION}/users/tasks/history`)
  },
}

// Badges API
export const badgeAPI = {
  getUserBadges: async () => {
    return await apiRequest(`${API_VERSION}/users/badges`)
  },

  getAllBadges: async () => {
    return await apiRequest(`${API_VERSION}/users/badges/available`)
  },
}

// Simulations API
export const simulationAPI = {
  getAll: async () => {
    return await apiRequest(`${API_VERSION}/simulations`)
  },

  getById: async (simulationId) => {
    return await apiRequest(`${API_VERSION}/simulations/${simulationId}`)
  },

  start: async (simulationId) => {
    return await apiRequest(`${API_VERSION}/simulations/${simulationId}/start`, {
      method: 'POST',
    })
  },

  submitDecision: async (simulationId, data) => {
    return await apiRequest(`${API_VERSION}/simulations/${simulationId}/decision`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  complete: async (simulationId, data) => {
    return await apiRequest(`${API_VERSION}/simulations/${simulationId}/complete`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  getHistory: async () => {
    return await apiRequest(`${API_VERSION}/simulations/progress/history`)
  },
}

// Notifications API
export const notificationAPI = {
  subscribe: async (subscription, preferences) => {
    return await apiRequest(`${API_VERSION}/notifications/subscribe`, {
      method: 'POST',
      body: JSON.stringify({ subscription, preferences }),
    })
  },

  unsubscribe: async (endpoint) => {
    return await apiRequest(`${API_VERSION}/notifications/unsubscribe`, {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    })
  },

  getPreferences: async () => {
    return await apiRequest(`${API_VERSION}/notifications/preferences`)
  },

  updatePreferences: async (preferences) => {
    return await apiRequest(`${API_VERSION}/notifications/preferences`, {
      method: 'PUT',
      body: JSON.stringify(preferences),
    })
  },

  getSubscriptions: async () => {
    return await apiRequest(`${API_VERSION}/notifications/subscriptions`)
  },

  sendTest: async () => {
    return await apiRequest(`${API_VERSION}/notifications/test`, {
      method: 'POST',
    })
  },
}

// Export API
export const exportAPI = {
  exportJSON: async () => {
    const data = await apiRequest(`${API_VERSION}/export/json`)
    return data
  },

  exportCSV: async () => {
    const csv = await apiRequest(`${API_VERSION}/export/csv`)
    return csv
  },

  exportPDF: async () => {
    const html = await apiRequest(`${API_VERSION}/export/pdf`)
    return html
  },

  getStats: async () => {
    return await apiRequest(`${API_VERSION}/export/stats`)
  },
}

// Offline Sync API
export const offlineAPI = {
  sync: async () => {
    return await apiRequest(`${API_VERSION}/offline/sync`)
  },

  checkUpdates: async (lastSync) => {
    return await apiRequest(`${API_VERSION}/offline/check-updates`, {
      method: 'POST',
      body: JSON.stringify({ lastSync }),
    })
  },
}

// Intelligence/Search API
export const intelligenceAPI = {
  search: async (query, userContext = {}) => {
    return await apiRequest(`${API_VERSION}/intelligence/search`, {
      method: 'POST',
      body: JSON.stringify({ query, userContext }),
    })
  },

  getRecommendations: async (userProfile) => {
    return await apiRequest(`${API_VERSION}/intelligence/recommendations`, {
      method: 'POST',
      body: JSON.stringify({ userProfile }),
    })
  },

  getEmergencyGuidance: async (situation, location, availableResources = []) => {
    return await apiRequest(`${API_VERSION}/intelligence/emergency`, {
      method: 'POST',
      body: JSON.stringify({ situation, location, availableResources }),
    })
  },

  getStatus: async () => {
    return await apiRequest(`${API_VERSION}/intelligence/status`)
  },
}

// Emergency API (public, no auth required)
export const emergencyAPI = {
  getContacts: async (country, latitude, longitude) => {
    return await apiRequest(`${API_VERSION}/emergency/contacts`, {
      method: 'POST',
      body: JSON.stringify({ country, latitude, longitude }),
    })
  },

  sendSOS: async (sosData) => {
    return await apiRequest(`${API_VERSION}/emergency/sos`, {
      method: 'POST',
      body: JSON.stringify(sosData),
    })
  },
}

// Onboarding API
export const onboardingAPI = {
  saveOnboarding: async (data) => {
    return await apiRequest(`${API_VERSION}/users/onboarding`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// Utility functions
export const utils = {
  setAuthToken,
  getAuthToken,
  clearAuth: () => {
    setAuthToken(null)
    localStorage.removeItem('surviveit_user')
  },

  // Check if user is authenticated
  isAuthenticated: () => !!getAuthToken(),

  // Handle API errors consistently
  handleAPIError: (error) => {
    if (error.status === 401) {
      // Unauthorized - clear token and redirect to login
      utils.clearAuth()
      window.location.href = '/auth'
      return
    }

    // Log error for debugging
    console.error('API Error:', error)

    // Return user-friendly message
    return error.message || 'An error occurred. Please try again.'
  },

  // Download helper for exports
  downloadFile: (content, filename, mimeType = 'application/json') => {
    const blob = new Blob([typeof content === 'string' ? content : JSON.stringify(content, null, 2)], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },
}

export default {
  authAPI,
  userAPI,
  moduleAPI,
  lessonAPI,
  tasksAPI,
  badgeAPI,
  simulationAPI,
  notificationAPI,
  exportAPI,
  offlineAPI,
  intelligenceAPI,
  emergencyAPI,
  onboardingAPI,
  utils,
}
